// components/SignUp.tsx
// NOTE: Auth screen error messages are always displayed in English regardless of user language preference
import React, { useState, useEffect } from 'react';
import { useSignUp, useSignIn } from '@clerk/clerk-react';
import { Loader2, ArrowLeft, Home, ClipboardList, Utensils, DollarSign, BookUser, HeartHandshake } from 'lucide-react';
import ErrorBanner from './ui/ErrorBanner';
import { logger } from '../utils/logger';
import { trackSignupComplete } from '../services/metaPixel';

interface SignUpProps {
  onBackToSignIn: () => void;
}

// Helper function to get user-friendly error message from Clerk errors
// NOTE: Auth screen errors are always in English regardless of user language preference
function getClerkErrorMessage(err: any): string {
  const errorCode = err.errors?.[0]?.code;
  const errorMessage = err.errors?.[0]?.longMessage || err.errors?.[0]?.message || err.message || '';
  
  // Map Clerk error codes to user-friendly messages (always English)
  if (errorCode === 'form_identifier_exists' || errorMessage.toLowerCase().includes('email address is taken')) {
    return 'This email is already registered. Please sign in instead.';
  }
  if (errorCode === 'form_password_pwned' || errorMessage.toLowerCase().includes('data breach')) {
    return 'Password must contain at least 8 characters, 1 lowercase, 1 uppercase, 1 special character';
  }
  if (errorCode === 'form_password_length_too_short' || errorMessage.toLowerCase().includes('password')) {
    return 'Password must contain at least 8 characters, 1 lowercase, 1 uppercase, 1 special character';
  }
  if (errorCode === 'form_code_incorrect' || errorMessage.toLowerCase().includes('incorrect code')) {
    return 'Incorrect code. Please check your email and enter the correct 6-digit code.';
  }
  if (errorCode === 'verification_expired' || errorMessage.toLowerCase().includes('expired')) {
    return 'Code expired. Please request a new one.';
  }
  if (errorCode === 'too_many_requests' || errorMessage.toLowerCase().includes('too many')) {
    return 'Too many attempts. Please wait a few minutes before trying again.';
  }
  
  // Default fallback - don't show raw technical error
  return 'Sign up failed. Please try again.';
}

const SignUp: React.FC<SignUpProps> = ({ onBackToSignIn }) => {
  const { signUp, setActive, isLoaded } = useSignUp();
  const { signIn, setActive: setActiveSignIn, isLoaded: signInLoaded } = useSignIn();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [verificationStep, setVerificationStep] = useState<'email' | null>(null);
  const [code, setCode] = useState('');
  const [isOAuthProcessing, setIsOAuthProcessing] = useState(false);
  const [hasCheckedOAuthRedirect, setHasCheckedOAuthRedirect] = useState(false);

  // Initialize native Google Sign-In plugin once at mount (required before login() calls)
  useEffect(() => {
    const isNative = typeof (window as any)?.Capacitor !== 'undefined'
      && (window as any)?.Capacitor?.isNativePlatform?.();
    if (isNative) {
      import('@capgo/capacitor-social-login').then(({ SocialLogin }) => {
        SocialLogin.initialize({
          google: {
            webClientId: '687792783542-b3fsfqlrq1vls826ou7vc0m03e73s3q6.apps.googleusercontent.com',
          },
        }).catch((err: any) => {
          console.error('[HelpyOAuth] SocialLogin.initialize() failed:', err?.message || err);
        });
      });
    }
  }, []);

  // Check for invite params and preserve them
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const isInvite = urlParams.get('invite') === 'true' || hashParams.get('invite') === 'true';
    const hid = urlParams.get('hid') || hashParams.get('hid');
    const uid = urlParams.get('uid') || hashParams.get('uid');
    
    if (isInvite && hid && uid) {
      // Preserve invite params in URL
      const inviteUrl = `/?invite=true&hid=${hid}&uid=${uid}`;
      window.history.replaceState({}, '', inviteUrl);
    }
  }, []);

  // Helper function to get redirect URL with invite params preserved
  const getRedirectUrl = () => {
    const baseUrl = window.location.origin + window.location.pathname;
    
    // Log what we're using
    console.log('🔵 [OAuth] window.location.origin:', window.location.origin);
    console.log('🔵 [OAuth] window.location.href:', window.location.href);
    console.log('🔵 [OAuth] Computed redirectUrl:', baseUrl);
    
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const isInvite = urlParams.get('invite') === 'true' || hashParams.get('invite') === 'true';
    const hid = urlParams.get('hid') || hashParams.get('hid');
    const uid = urlParams.get('uid') || hashParams.get('uid');
    
    if (isInvite && hid && uid) {
      return `${baseUrl}?invite=true&hid=${hid}&uid=${uid}`;
    }
    return baseUrl.split('?')[0];
  };

  // Handle OAuth redirect completion
  useEffect(() => {
    if (!isLoaded || !signUp) return;

    // Check if we're returning from an OAuth redirect
    const handleOAuthRedirect = async () => {
      // Only check once per signUp session to avoid duplicate processing
      if (hasCheckedOAuthRedirect) return;

      try {
        setHasCheckedOAuthRedirect(true);

        // Check if signUp is complete after OAuth
        if (signUp.status === 'complete' && signUp.createdSessionId) {
          setIsOAuthProcessing(true);
          // Track signup completion for Meta Pixel (Lead event)
          trackSignupComplete({
            content_name: 'Trial Signup - Google OAuth',
            content_category: 'oauth_signup',
          });
          await setActive({ session: signUp.createdSessionId });
          // User will be redirected by Auth component
          return;
        }

        // If signUp needs additional requirements (like CAPTCHA), Clerk will handle it
        // The CAPTCHA widget will be automatically mounted in the container
        if (signUp.status === 'missing_requirements') {
          // Clerk will automatically show CAPTCHA in the #clerk-captcha container
          // Reset processing state so user can interact with CAPTCHA
          setIsOAuthProcessing(false);
          logger.log('OAuth completed but missing requirements - CAPTCHA should appear');
        } else if (signUp.status === 'abandoned') {
          // OAuth was cancelled or failed
          setIsOAuthProcessing(false);
          setHasCheckedOAuthRedirect(false);
        }
      } catch (error: any) {
        logger.error('OAuth redirect handling error:', error);
        
        // Check for external_account_exists error (Clerk's way of saying user already exists)
        const hasExternalAccountError = error.errors?.some(
          (e: any) => e.code === 'external_account_exists' || 
                      e.code === 'form_identifier_exists' ||
                      e.message?.toLowerCase().includes('already exists')
        );

        if (hasExternalAccountError && signInLoaded && signIn) {
          // Use Clerk's transfer mechanism to switch to sign-in
          try {
            logger.log('Existing account detected, transferring to sign-in...');
            
            // Transfer the OAuth flow from sign-up to sign-in
            await signIn.create({ transfer: true });
            
            // If transfer successful, continue with sign-in OAuth
            const redirectUrl = getRedirectUrl();
            
            await signIn.authenticateWithRedirect({
              strategy: 'oauth_google',
              redirectUrl: redirectUrl,
              redirectUrlComplete: window.location.origin,
            });
            return;
          } catch (transferError: any) {
            logger.error('Transfer to sign-in failed:', transferError);
            // Fallback: try direct sign-in OAuth
            try {
              const redirectUrl = getRedirectUrl();
              
              await signIn.authenticateWithRedirect({
                strategy: 'oauth_google',
                redirectUrl: redirectUrl,
                redirectUrlComplete: window.location.origin,
              });
              return;
            } catch (signInError: any) {
              logger.error('Sign-in OAuth error:', signInError);
              setError('Account already exists. Please use Sign In instead.');
            }
          }
        } else {
          setError('Failed to complete sign up. Please try again.');
        }
        
        setIsOAuthProcessing(false);
        setHasCheckedOAuthRedirect(false);
      }
    };

    handleOAuthRedirect();
  }, [isLoaded, signUp, setActive, hasCheckedOAuthRedirect, signIn, signInLoaded]);

  // Handle sign-in OAuth redirect completion
  useEffect(() => {
    if (!signInLoaded || !signIn) return;

    const handleSignInOAuthRedirect = async () => {
      try {
        // Check if sign-in is complete after OAuth
        if (signIn.status === 'complete' && signIn.createdSessionId) {
          setIsOAuthProcessing(true);
          await setActiveSignIn({ session: signIn.createdSessionId });
          // User will be redirected by Auth component
          return;
        }
      } catch (error: any) {
        logger.error('Sign-in OAuth redirect handling error:', error);
        setError('Failed to sign in. Please try again.');
        setIsOAuthProcessing(false);
      }
    };

    handleSignInOAuthRedirect();
  }, [signInLoaded, signIn, setActiveSignIn]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;

    // Validate that email is provided
    if (!formData.email) {
      setError('Please provide an email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Preserve invite params before signup
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const isInvite = urlParams.get('invite') === 'true' || hashParams.get('invite') === 'true';
      const hid = urlParams.get('hid') || hashParams.get('hid');
      const uid = urlParams.get('uid') || hashParams.get('uid');
      
      if (isInvite && hid && uid) {
        window.history.replaceState({}, '', `/?invite=true&hid=${hid}&uid=${uid}`);
      }

      await signUp.create({
        firstName: formData.firstName,
        lastName: formData.lastName,
        emailAddress: formData.email,
        password: formData.password,
      });

      // Check if verification is needed based on unverifiedFields array
      const hasUnverifiedEmail = signUp.unverifiedFields && signUp.unverifiedFields.length > 0 && 
        signUp.unverifiedFields.some(field => field === 'email_address');
      
      if (hasUnverifiedEmail && formData.email) {
        // Send email verification code
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        setVerificationStep('email');
      } else if (signUp.status === 'complete') {
        // Preserve invite params before setActive
        if (isInvite && hid && uid) {
          window.history.replaceState({}, '', `/?invite=true&hid=${hid}&uid=${uid}`);
        }
        // Track signup completion for Meta Pixel (Lead event)
        trackSignupComplete({
          content_name: 'Trial Signup - Email',
          content_category: 'email_signup',
        });
        // No verification needed, sign up is complete
        await setActive({ session: signUp.createdSessionId! });
        // User will be redirected by Auth component
      } else {
        // Unexpected status - show error
        setError('Account creation completed but requires additional setup. Please try signing in.');
      }
    } catch (err: any) {
      logger.error('Signup error:', err);
      setError(getClerkErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;

    setIsSubmitting(true);
    setError('');

    try {
      if (verificationStep !== 'email') {
        setError('Invalid verification step');
        setIsSubmitting(false);
        return;
      }

      const result = await signUp.attemptEmailAddressVerification({
        code,
      });

      if (result.status === 'complete') {
        // Preserve invite params before setActive
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const isInvite = urlParams.get('invite') === 'true' || hashParams.get('invite') === 'true';
        const hid = urlParams.get('hid') || hashParams.get('hid');
        const uid = urlParams.get('uid') || hashParams.get('uid');
        
        if (isInvite && hid && uid) {
          window.history.replaceState({}, '', `/?invite=true&hid=${hid}&uid=${uid}`);
        }
        
        // Track signup completion for Meta Pixel (Lead event)
        trackSignupComplete({
          content_name: 'Trial Signup - Email Verified',
          content_category: 'email_signup',
        });
        
        await setActive({ session: result.createdSessionId });
        // User will be redirected by Auth component
      } else {
        // Handle other statuses
        switch (result.status) {
          case 'missing_requirements':
            setError('Additional information is required. Please check your email or phone for verification instructions.');
            break;
          case 'abandoned':
            setError('Verification was cancelled. Please try again.');
            break;
          default:
            setError(`Verification status: ${result.status}. Please try again or contact support if the issue persists.`);
        }
      }
    } catch (err: any) {
      logger.error('Verification error:', err);
      setError(getClerkErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Google OAuth signup - uses native SDK on Android/iOS, redirect flow on web.
  const handleGoogleSignUp = async () => {
    if (!signUp || !isLoaded) {
      // If signUp isn't ready, wait a bit and try again
      setTimeout(() => handleGoogleSignUp(), 100);
      return;
    }

    // Prevent duplicate clicks
    if (isOAuthProcessing) {
      return;
    }

    setIsOAuthProcessing(true);
    setError('');
    setHasCheckedOAuthRedirect(false);

    const isNative = typeof (window as any)?.Capacitor !== 'undefined'
      && (window as any)?.Capacitor?.isNativePlatform?.();

    try {
      if (isNative) {
        // ── Native: use Google Sign-In SDK (no browser at all) ──
        const { SocialLogin } = await import('@capgo/capacitor-social-login');

        console.log('🔵 [OAuth] calling SocialLogin.login()...');
        const result = await SocialLogin.login({
          provider: 'google',
          options: {},
        });

        console.log('🔵 [OAuth] SocialLogin.login() result:', JSON.stringify(result));
        const idToken = (result as any)?.result?.idToken;
        if (!idToken) {
          throw new Error('No ID token returned from Google Sign-In');
        }

        console.log('🔵 [OAuth] Got native Google ID token, exchanging for Clerk ticket');

        // Exchange the Google ID token for a Clerk sign-in ticket via backend
        // (backend creates the Clerk user if it doesn't exist)
        const apiUrl = import.meta.env?.VITE_API_URL || '';
        const res = await fetch(`${apiUrl}/api/google-native-signin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken }),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Backend returned ${res.status}`);
        }

        const { ticket } = await res.json();

        // Use the ticket to create a Clerk session (signIn, not signUp, because
        // the backend already created the Clerk user if needed)
        if (!signInLoaded || !signIn) {
          throw new Error('Sign-in not ready');
        }

        const signInResult = await signIn.create({
          strategy: 'ticket',
          ticket,
        });

        if (signInResult.status === 'complete') {
          // Track signup for Meta Pixel
          trackSignupComplete({
            content_name: 'Trial Signup - Google OAuth (Native)',
            content_category: 'oauth_signup',
          });
          await setActiveSignIn({ session: signInResult.createdSessionId });
          console.log('🔵 [OAuth] Native Google sign-up complete, session activated');
        } else {
          console.error('🔴 [OAuth] Unexpected sign-in status:', signInResult.status);
          setError('Sign-up incomplete. Please try again.');
          setIsOAuthProcessing(false);
        }
      } else {
        // ── Web: standard Clerk redirect flow ──
        const redirectUrl = getRedirectUrl();
        console.log('🔵 [OAuth] Initiating Google OAuth with redirectUrl:', redirectUrl);
        console.log('🔵 [OAuth] redirectUrlComplete:', redirectUrl);
        
        await signUp.authenticateWithRedirect({
          strategy: 'oauth_google',
          redirectUrl: redirectUrl,
          redirectUrlComplete: window.location.origin,
        });
      }
    } catch (error: any) {
      console.error('🔴 [OAuth] Error:', error);
      console.error('🔴 [OAuth] Error details:', JSON.stringify(error, null, 2));
      logger.error('Google OAuth error:', error);

      // Don't show error if user cancelled the native picker
      if (error?.message?.toLowerCase().includes('cancel') || error?.message?.toLowerCase().includes('dismissed')) {
        setIsOAuthProcessing(false);
        setHasCheckedOAuthRedirect(false);
        return;
      }
      
      // Check for external_account_exists error BEFORE redirect (web flow only)
      const hasExternalAccountError = error.errors?.some(
        (e: any) => e.code === 'external_account_exists' || 
                    e.code === 'form_identifier_exists' ||
                    e.message?.toLowerCase().includes('already exists')
      );

      if (hasExternalAccountError && signInLoaded && signIn) {
        // Use Clerk's transfer mechanism
        try {
          logger.log('Existing account detected before redirect, transferring to sign-in...');
          await signIn.create({ transfer: true });
          
          const redirectUrl = getRedirectUrl();
          
          await signIn.authenticateWithRedirect({
            strategy: 'oauth_google',
            redirectUrl: redirectUrl,
            redirectUrlComplete: window.location.origin,
          });
          return;
        } catch (signInError: any) {
          logger.error('Sign-in OAuth error:', signInError);
          setError('Account already exists. Please use Sign In instead.');
        }
      } else {
        setError('Failed to initiate Google sign-in. Please try again.');
      }
      
      setIsOAuthProcessing(false);
      setHasCheckedOAuthRedirect(false);
    }
  };

  if (verificationStep) {
    return (
      <div className="min-h-screen w-full flex flex-col p-6 pt-16 page-fade-in auth-gradient-bg">
        <div className="w-full max-w-md mx-auto">
          {/* Logo */}
          <div className="mb-10">
            <img 
              src="/helpy-logo-blue.png" 
              alt="Helpy" 
              className="h-12 w-auto"
            />
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-display font-bold text-foreground mb-2">Check your email</h1>
            <p className="text-body font-medium text-muted-foreground">
              We sent a verification code to<br />
              <span className="text-foreground font-medium">{formData.email}</span>
            </p>
          </div>

          <ErrorBanner 
            error={error} 
            onDismiss={() => setError('')} 
            title="Error"
          />

          <form onSubmit={handleVerify} className="space-y-5">
            <div>
              <label className="text-caption text-muted-foreground mb-2 block">
                Verification Code
              </label>
              <input
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setCode(value);
                }}
                placeholder="Enter 6-digit code"
                required
                maxLength={6}
                className="w-full px-4 py-3.5 rounded-xl bg-white border border-border text-foreground placeholder:text-muted-foreground focus:border-primary outline-none transition-all text-body font-medium tracking-widest text-center"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 bg-primary text-primary-foreground rounded-xl text-body font-semibold shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Verifying...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </form>

          <button className="mt-6 text-body text-primary font-medium">
            Didn't receive a code? Resend
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col p-6 pt-16 page-fade-in auth-gradient-bg">
      <div className="w-full max-w-md mx-auto">
        {/* Logo */}
        <div className="mb-10">
          <img 
            src="/helpy-logo-blue.png" 
            alt="Helpy" 
            className="h-12 w-auto"
          />
        </div>

        {/* Back button */}
        <button
          onClick={onBackToSignIn}
          className="flex items-center gap-2 text-muted-foreground mb-6"
        >
          <ArrowLeft size={18} />
          <span className="text-body font-medium">Back to Sign In</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-display font-bold text-foreground">Create Account</h1>
        </div>

        <ErrorBanner 
          error={error} 
          onDismiss={() => setError('')} 
          title="Error"
        />

        {/* Google Sign Up Button */}
        <button
          type="button"
          onClick={handleGoogleSignUp}
          disabled={!isLoaded || isOAuthProcessing}
          className="w-full py-3.5 bg-white border border-border rounded-2xl text-body font-medium text-foreground flex items-center justify-center gap-3 disabled:opacity-50 mb-5"
        >
          {isOAuthProcessing ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Processing...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-5">
          <div className="flex-1 h-px bg-border"></div>
          <span className="text-caption text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border"></div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-caption text-muted-foreground mb-2 block">
                First Name
              </label>
              <input
                type="text"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="John"
                required
                className="w-full px-4 py-3.5 rounded-2xl bg-white border border-border text-foreground placeholder:text-muted-foreground focus:border-primary outline-none transition-all text-body font-medium"
              />
            </div>
            <div>
              <label className="text-caption text-muted-foreground mb-2 block">
                Last Name
              </label>
              <input
                type="text"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Doe"
                required
                className="w-full px-4 py-3.5 rounded-2xl bg-white border border-border text-foreground placeholder:text-muted-foreground focus:border-primary outline-none transition-all text-body font-medium"
              />
            </div>
          </div>

          <div>
            <label className="text-caption text-muted-foreground mb-2 block">
              Email
            </label>
            <input
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              required
              className="w-full px-4 py-3.5 rounded-2xl bg-white border border-border text-foreground placeholder:text-muted-foreground focus:border-primary outline-none transition-all text-body font-medium"
            />
          </div>

          <div>
            <label className="text-caption text-muted-foreground mb-2 block">
              Password
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Enter password"
              required
              className="w-full px-4 py-3.5 rounded-2xl bg-white border border-border text-foreground placeholder:text-muted-foreground focus:border-primary outline-none transition-all text-body font-medium"
            />
            <p className="text-caption text-muted-foreground mt-2 ml-1">
              Min. 8 characters, 1 lowercase, 1 uppercase, 1 special character
            </p>
          </div>

          {/* Clerk CAPTCHA widget container */}
          <div id="clerk-captcha"></div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3.5 bg-primary text-primary-foreground rounded-2xl text-body font-semibold shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="mt-6 text-body font-medium text-muted-foreground">
          Already have an account?{' '}
          <button onClick={onBackToSignIn} className="text-primary font-semibold">
            Sign In
          </button>
        </p>

        {/* Free to use banner */}
        <div className="mt-8 rounded-xl border border-primary/30 bg-primary/8 px-5 py-4 text-center">
          <p className="text-body font-medium text-foreground">Helpy is free to use, try today</p>
        </div>

        {/* Features Section */}
        <div className="mt-8 mb-8">
          <h2 className="text-xl font-bold text-foreground mb-4">Helpy's Features</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            Helpy is the home management app that simplifies daily life. It gathers meals, tasks, and spending in one spot. The ideal family collaboration app for families and helpers to work as a team.
          </p>

          <div className="grid grid-cols-1 gap-4">
            {[
              {
                icon: Home,
                title: 'Home',
                body: 'Discover a serene dashboard that unites your family\'s plans, reminders, and essential tools into a single, intuitive overview for effortless home management.',
                features: 'Family Board, Widgets with quick action buttons, App Translations',
              },
              {
                icon: ClipboardList,
                title: 'To Do',
                body: 'Keep everyone in sync with a shared task and shopping list.',
                features: 'Task list with due dates and recurring settings. Shopping list with Shopping Mode',
              },
              {
                icon: Utensils,
                title: 'Meals',
                body: 'Family meal scheduler to organize the week and guide your helper.',
                features: 'Meal planner for adults and kids. Quick recipe search on YouTube. RSVP system to join the meal. PDF export for printing.',
              },
              {
                icon: DollarSign,
                title: 'Expenses',
                body: 'Stay on budget with a simple family expense tracker. It works as a household expense manager that lets you snap and track receipts in seconds.',
                features: 'Expenses list and summary. AI receipt scanner',
              },
              {
                icon: BookUser,
                title: 'Family',
                body: 'A "Family Book" storing all of the important information about the family for everyone to know. Important and common places that you often visit (such as school, doctors, etc.), and how to do things your way, all on one page.',
                features: 'Places and practices list, including contact details and instructions, and notes.',
              },
              {
                icon: HeartHandshake,
                title: 'Helper',
                body: 'Nestled in the Family page. Managing salary slips has never been easier! Generate the slip in one click and sign it digitally!',
                features: "Helper's start date, salary, and salary slip",
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="relative flex flex-col rounded-2xl bg-white/60 border border-border p-5"
                >
                  <div className="absolute right-5 top-5 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="pr-8 text-sm font-semibold text-foreground mb-1.5">
                    {item.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                    {item.body}
                  </p>
                  <div className="border-t border-border mb-3" />
                  <p className="text-xs text-primary leading-relaxed">
                    {item.features}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;